import { Col, Row } from "antd";
import { get, set } from "lodash";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdWorkOutline } from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  JOB_LIST,
  MILTIS_PRODUCT_CODES,
  SOCIO_PROFESSIONAL_CATEGORIES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Activity = ({
  setPercent,
  disabled,
  setDisabled,
  isFieldRequired,
  initialValues,
  bindField,
  subscription,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const isAuraTer = useMemo(
    () =>
      get(subscription, "insurancePlan.product") ===
      MILTIS_PRODUCT_CODES.AURA_TER,
    [subscription],
  );

  const PROFESSION_CODE_MAP = useMemo(
    () => ({
      [SOCIO_PROFESSIONAL_CATEGORIES.FARMERS]: "500",
      [SOCIO_PROFESSIONAL_CATEGORIES.ARTISANS]: "410",
      [SOCIO_PROFESSIONAL_CATEGORIES.TRADERS_AND_SIMILAR]: "430",
      [SOCIO_PROFESSIONAL_CATEGORIES.ENTREPRENEURS]: "420",
      [SOCIO_PROFESSIONAL_CATEGORIES.LIBERAL_PROFESSIONS_AND_SIMILAR]: "450",
      [SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES]:
        "280",
      [SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES]: "220",
      [SOCIO_PROFESSIONAL_CATEGORIES.EMPLOYEES_AND_SUPERVISORS]: "242",
      [SOCIO_PROFESSIONAL_CATEGORIES.WORKERS]: "243",
      [SOCIO_PROFESSIONAL_CATEGORIES.RETIREES]: "700",
      [SOCIO_PROFESSIONAL_CATEGORIES.PEOPLE_WITHOUT_PROFESSIONAL_ACTIVITY]:
        "290",
    }),
    [],
  );

  useEffect(() => {
    if (isAuraTer) {
      const currentProfession = get(
        initialValues,
        "project.contact.profession",
      );
      const defaultProfession =
        SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES;

      if (!currentProfession) {
        set(initialValues, "project.contact.profession", defaultProfession);
        set(
          initialValues,
          "project.contact.exerciseFrame",
          PROFESSION_CODE_MAP[defaultProfession],
        );
      }

      if (setDisabled) {
        setDisabled((prev) => ({
          ...prev,
          project: {
            ...prev?.project,
            contact: {
              ...prev?.project?.contact,
              profession: true,
            },
          },
        }));
      }
    }
  }, [isAuraTer, initialValues, setDisabled, PROFESSION_CODE_MAP]);

  const PROFESSIONAL_SITUATION_OPTIONS = useMemo(
    () => [
      {
        value: JOB_LIST.ACTIVE,
        label:
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY
            ?.PROFESSIONAL_SITUATION?.OPTIONS?.ACTIVE || "En activité",
      },
      {
        value: JOB_LIST.RETIRED,
        label:
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY
            ?.PROFESSIONAL_SITUATION?.OPTIONS?.RETIRED || "Retraité",
      },
    ],
    [SUBSCRIPTION],
  );

  const PROFESSION_OPTIONS = useMemo(
    () => [
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.FARMERS,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.FARMERS ||
          "Agriculteurs exploitants",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.ARTISANS,
        label: SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.ARTISANS || "Artisans",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.TRADERS_AND_SIMILAR,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.TRADERS_AND_SIMILAR ||
          "Commerçants et assimilés",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.ENTREPRENEURS,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.ENTREPRENEURS ||
          "Chefs d'entreprise",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.LIBERAL_PROFESSIONS_AND_SIMILAR,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS
            ?.LIBERAL_PROFESSIONS_AND_SIMILAR ||
          "Professions libérales et assimilées",
      },
      {
        value:
          SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS
            ?.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES ||
          "Cadres et employés de la fonction publique",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.EXECUTIVES ||
          "Cadre d'entreprise",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.EMPLOYEES_AND_SUPERVISORS,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.EMPLOYEES_AND_SUPERVISORS ||
          "Employés et agents de maîtrise",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.WORKERS,
        label: SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.WORKERS || "Ouvriers",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.RETIREES,
        label: SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.RETIREES || "Retraités",
      },
      {
        value:
          SOCIO_PROFESSIONAL_CATEGORIES.PEOPLE_WITHOUT_PROFESSIONAL_ACTIVITY,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS
            ?.PEOPLE_WITHOUT_PROFESSIONAL_ACTIVITY ||
          "Personnes sans activité professionnelle",
      },
    ],
    [SUBSCRIPTION],
  );

  const professionPath = "project.contact.profession";
  const professionField = bindField(professionPath, {
    label: SUBSCRIPTION.FORM.PROFESSION.LABEL,
    required: isFieldRequired(professionPath),
    disabled: isAuraTer || get(disabled, "project.contact.profession"),
    onChange: (value) => {
      const exerciseFrameCode = PROFESSION_CODE_MAP[value];
      if (exerciseFrameCode) {
        set(
          initialValues,
          "project.contact.exerciseFrame",
          exerciseFrameCode,
        );
      } else {
        set(initialValues, "project.contact.exerciseFrame", null);
      }
    },
    onBlur: () => setPercent(initialValues),
  });

  const professionalSituationPath =
    "project.contact.fields.professionalSituation";
  const professionalSituationField = bindField(professionalSituationPath, {
    label:
      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY?.PROFESSIONAL_SITUATION
        ?.LABEL || "Situation professionnelle",
    required: isFieldRequired(professionalSituationPath),
    disabled: get(disabled, "project.contact.fields.professionalSituation"),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={professionField.path}
              label={professionField.label}
              required={professionField.required}
              disabled={professionField.disabled}
            >
              {(field) => (
                <Select
                  options={PROFESSION_OPTIONS}
                  icon={<MdWorkOutline size={16} />}
                  placeholder={SUBSCRIPTION.FORM.PROFESSION.PLACEHOLDER}
                  label={professionField.label}
                  defaultValue={
                    professionField.defaultValue ||
                    (isAuraTer
                      ? SOCIO_PROFESSIONAL_CATEGORIES
                          .EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES
                      : undefined)
                  }
                  {...field}
                  onChange={professionField.onChange}
                  onBlur={professionField.onBlur}
                  optional={!professionField.required}
                />
              )}
            </FormField>
          </Col>
          {isAuraTer && (
            <Col lg={24} xs={24}>
              <FormField
                path={professionalSituationField.path}
                label={professionalSituationField.label}
                required={professionalSituationField.required}
                disabled={professionalSituationField.disabled}
              >
                {(field) => (
                  <Select
                    options={PROFESSIONAL_SITUATION_OPTIONS}
                    icon={<MdWorkOutline size={16} />}
                    placeholder={
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY
                        ?.PROFESSIONAL_SITUATION?.PLACEHOLDER || "Sélectionnez"
                    }
                    label={professionalSituationField.label}
                    defaultValue={professionalSituationField.defaultValue}
                    {...field}
                    onChange={professionalSituationField.onChange}
                    onBlur={professionalSituationField.onBlur}
                  />
                )}
              </FormField>
            </Col>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Activity;
